/**
 * Handicraft/Souvenir List Component
 *
 * Single Responsibility: Manages and displays a list of handicraft/souvenir items
 * Follows SOLID principles:
 * - Single Responsibility: Only responsible for rendering a list of handicrafts/souvenirs
 * - Open/Closed: Open for extension via props, closed for modification
 * - Dependency Inversion: Depends on HandicraftItem component interface, not concrete implementation
 */

import React from 'react';
import { spacing } from '@/design-system/utils';
import { HandicraftItem } from './HandicraftItem';
import { HandicraftSouvenir } from './types';

export interface HandicraftListProps {
  /**
   * Array of handicraft/souvenir items to display
   */
  items: HandicraftSouvenir[];
  /**
   * Place name for image fetching context
   */
  placeName?: string;
  /**
   * Whether to use mobile layout
   */
  isMobile?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Custom gap spacing between items
   */
  gap?: number;
}

/**
 * Handicraft/Souvenir List Component
 * Renders a list of handicraft/souvenir items with consistent spacing
 */
export const HandicraftList: React.FC<HandicraftListProps> = ({
  items,
  placeName,
  isMobile = false,
  className,
  gap,
}) => {
  if (!items || items.length === 0) {
    return null;
  }

  // Use provided gap or default based on mobile/desktop
  const listGap = gap !== undefined ? gap : isMobile ? spacing[3] : spacing[4];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: listGap,
        padding: `0 ${spacing[3]}px ${spacing[3]}px`,
      }}
      className={className}
    >
      {items.map(item => (
        <HandicraftItem
          key={item.id}
          item={item}
          placeName={placeName}
          isMobile={isMobile}
        />
      ))}
    </div>
  );
};
